import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import { useQueryClient } from '@tanstack/react-query';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Layout from '../../../componentsv2/Layout';
import Section from '../../../componentsv2/Section';
import Typography from '../../../componentsv2/Typography';
import Button from '../../../componentsv2/Button';
import Modal from '../../../componentsv2/Modal';
import { MessageSentIcon } from '../../../components/Icons';
import { submitBankingRequest } from '../../../services/banking';
import { useAppSelector } from '../../../store';
import { useLazyTranslation } from '../../../hooks/i18n';
import { useSnackbar } from '../../../context/snackbar';
import { BANKING_REQUESTS_QUERY_KEY, useBankingRequests } from '../../../hooks/useBanking';
import { bankingEvents } from '../../../constants/events';
import { HELP_CENTER_URL } from '../../../constants/common';
import { DASHBOARD } from '../../../constants/auth';
import { UserRole } from '../../../types/companyUser';
import { isActivatingGlobalAccount, isGlobalAccountActive } from '../../../utils/onboarding';
import BankingDashboard from '../../Banking/Dashboard';
import { COLORS } from '../../../themev2';
import BankingPageSkeleton from './skeleton';
import GlobalAccountEmptyState from './GlobalAccountEmptyState';

const BankingPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t, isLoading: isLoadingTranslations } = useLazyTranslation(['banking-page', 'banking']);
  const { showSnackbar } = useSnackbar();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const companyUser = useAppSelector((state) => state.companyUser);
  const selectedCompany = companyUser.companyUser?.selectedCompany;
  const isVisualizer = companyUser.companyUser?.selectedCompanyRole === UserRole.VISUALIZER;
  const isActivating = isActivatingGlobalAccount(selectedCompany);

  const { data: bankingRequests, isInitialLoading: isLoadingBankingRequests } = useBankingRequests({
    companyId: selectedCompany?.id,
  });

  useEffect(() => {
    logEvent(bankingEvents.VIEW_PAGE);
  }, []);

  if (isLoadingTranslations || isLoadingBankingRequests) return <BankingPageSkeleton />;

  if (isGlobalAccountActive(selectedCompany) && selectedCompany?.id)
    return <BankingDashboard companyId={selectedCompany.id} accountId={selectedCompany.accounts?.[0]?.id!} />;

  const handleOpenAccount = () => {
    if (!selectedCompany?.id) return;
    setIsSubmitting(true);
    submitBankingRequest(selectedCompany.id)
      .then(() => {
        logEvent(bankingEvents.SUBMIT_REQUEST);
        setShowConfirmationModal(true);
        queryClient.invalidateQueries({ queryKey: [BANKING_REQUESTS_QUERY_KEY] });
      })
      .catch(() => {
        showSnackbar(t('banking-page:emptyState.error'), 'error');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleDiscoverMore = () => {
    logEvent(bankingEvents.CLICK_DISCOVER_MORE);
    window.open(HELP_CENTER_URL, '_blank', 'noopener,noreferrer');
  };

  const handleGoToHome = () => {
    logEvent(bankingEvents.CLICK_GO_TO_HOME);
    navigate(DASHBOARD);
  };

  return (
    <Layout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {isActivating && (
          <Section
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px' }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('banking-page:pendingBanner.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.gray[1100] }}>
                {t('banking-page:pendingBanner.description')}
              </Typography>
            </Box>
            <Button color="secondary" isFullHeight={false} onClick={handleGoToHome}>
              {t('banking-page:pendingBanner.cta')}
            </Button>
          </Section>
        )}
        <GlobalAccountEmptyState
          canRequest={!isActivating && !isVisualizer}
          hasBankingRequest={!isActivating && !!bankingRequests?.length}
          isSubmitting={isSubmitting}
          onOpenAccount={handleOpenAccount}
          onDiscoverMore={handleDiscoverMore}
        />
      </Box>
      <Modal
        isAutoHeight
        isOpen={showConfirmationModal}
        handleClose={() => {
          setShowConfirmationModal(false);
        }}
      >
        <Grid container rowSpacing={2}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <MessageSentIcon />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" textAlign="center" fontWeight={700}>
              {t('banking-page:confirmationModal.title')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body1"
              textAlign="center"
              sx={{ color: COLORS.gray[1100], paddingBottom: '1em' }}
            >
              {t('banking-page:confirmationModal.description')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button fullWidth onClick={() => setShowConfirmationModal(false)} color="secondary">
              {t('banking-page:confirmationModal.btn')}
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </Layout>
  );
};

export default BankingPage;
